import { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  CaffeineBreakdown,
  Intolerance,
  GeneDataGroupType,
  useOverviewQuery,
} from "../../../api/smartmelts-api/graphql/generated";
import { CaffeineIcon } from "../../../assets/icons/overview/caffeine-icon";
import { GlutenIcon } from "../../../assets/icons/overview/gluten-icon";
import { LactoseIcon } from "../../../assets/icons/overview/lactose-icon";
import { PRIMARY_COLOR } from "../../../theme/theme";
import { NutritionContext } from "../context/nutrition-context";

const OVERVIEW_POSITIVE_COLOR = PRIMARY_COLOR;
const OVERVIEW_NORMAL_COLOR = "#3A6D79";
const OVERVIEW_NEGATIVE_COLOR = "#911B58";

const COLOR_DICTIONARY_INTOLERANCE: Record<Intolerance, string> = {
  HIGH_RISK: OVERVIEW_NEGATIVE_COLOR,
  MODERATE_RISK: OVERVIEW_NORMAL_COLOR,
  NO_RISK: OVERVIEW_POSITIVE_COLOR,
};
const COLOR_DICTIONARY_BREAKDOWN: Record<CaffeineBreakdown, string> = {
  SLOW: OVERVIEW_NEGATIVE_COLOR,
  NORMAL: OVERVIEW_NORMAL_COLOR,
};

export const useTolerances = () => {
  const { isDemo, scrollTop } = useContext(NutritionContext);
  const { t } = useTranslation();
  const { data, loading } = useOverviewQuery({
    variables: { demo: isDemo },
    onCompleted: () => {
      scrollTop();
    },
  });
  return {
    loading,
    gluten: {
      name: t("overview.gluten.name"),
      label: t(`overview.gluten.intolerance.${data?.overview.gluten}`),
      labelColor: COLOR_DICTIONARY_INTOLERANCE[data?.overview.gluten ?? Intolerance.HIGH_RISK],
      intolerance: data?.overview.gluten,
      icon: GlutenIcon,
    },
    lactose: {
      name: t("overview.lactose.name"),
      label: t(`overview.lactose.intolerance.${data?.overview.lactose}`),
      labelColor: COLOR_DICTIONARY_INTOLERANCE[data?.overview.lactose ?? Intolerance.HIGH_RISK],
      intolerance: data?.overview.lactose,
      icon: LactoseIcon,
    },
    caffeine: {
      name: t("overview.caffeine.name"),
      label: t(`overview.caffeine.breakdown.${data?.overview.caffeine}`),
      labelColor: COLOR_DICTIONARY_BREAKDOWN[data?.overview.caffeine ?? CaffeineBreakdown.NORMAL],
      breakdown: data?.overview.caffeine,
      icon: CaffeineIcon,
    },
  };
};

export const useGeneDataGroupTolerances = () => {
  const tolerances = useTolerances();

  return {
    loading: tolerances.loading,
    [GeneDataGroupType.GLUTEN]: tolerances.gluten,
    [GeneDataGroupType.LACTOSE]: tolerances.lactose,
  };
};
